import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { useGetOrganizationBySlug } from '@/api/generated';
import Field from '@/components/fields/Field';
import TextInput from '@/components/fields/TextInput';
import CountryDropdownSelect from '@/views/fields/CountryDropdownSelect/CountryDropdownSelect';
import OfficeDropdownSelect from '@/views/fields/OfficeDropdownSelect/OfficeDropdownSelect';
import OrganizationDropdownSelect from '@/views/fields/OrganizationDropdownSelect/OrganizationDropdownSelect';

const SubOrgBasicInfoTab: React.FC = () => {
  const { t } = useTranslation('organizations');
  const { watch } = useFormContext();
  const organizationSlug = watch('organization');
  const { data } = useGetOrganizationBySlug(
    organizationSlug!,
    { isFull: true },
    {
      query: {
        enabled: !!organizationSlug
      }
    }
  );

  const allowedCountries = useMemo(() => data?.countries || [], [data]);
  const allowedOffices = useMemo(() => data?.offices || [], [data]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        p: 4,
        pt: 0,
        '& .MuiFormControl-root': {
          p: 0
        }
      }}
      gap={3}
    >
      <Field
        label={t('layouts.organizations.create.fields.subOrganizationName')}
        component={TextInput}
        name="name"
        withFormControl
        innerProps={{
          markAsRequired: true,
          placeholder: [
            t('layouts.organizations.create.untitled'),
            t('layouts.organizations.create.items.suborganization')
          ].join(' ')
        }}
      />
      <Box />
      <Field
        component={TextInput}
        name="legalName"
        label={t('layouts.organizations.create.fields.subOrganizationLegalName')}
        markAsRequired
        withFormControl
      />

      <Field
        component={OrganizationDropdownSelect}
        name="organization"
        label={t('layouts.organizations.create.fields.organization')}
        markAsRequired
        innerProps={{ isMulti: false, usePortal: true, isDisabled: true }}
        withFormControl
      />

      <Field
        component={OfficeDropdownSelect}
        name="offices"
        label={t('layouts.organizations.create.fields.office')}
        markAsRequired
        innerProps={{ isMulti: false, usePortal: true, allowedOffices }}
        withFormControl
      />

      <Field
        component={CountryDropdownSelect}
        name="countries"
        label={t('layouts.organizations.create.fields.country')}
        markAsRequired
        withFormControl
        innerProps={{
          isMulti: false,
          usePortal: true,
          allowedCountries
        }}
      />
    </Box>
  );
};

export default SubOrgBasicInfoTab;
